import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "react-oidc-context";
import { logout } from "../store/slices/appAuthSlice";
import { AppDispatch, RootState } from "../store/store";

/**
 * IdleManager: ends the session after a period of inactivity.
 *
 * The app holds a 30-day ZITADEL refresh token in localStorage, so without this
 * an abandoned browser stays signed in for a month. This is the client-side half
 * of that control — the authoritative half is ZITADEL's own session lifetime.
 *
 * Activity is shared across tabs through localStorage: each tab writes the
 * timestamp of the last interaction it saw, and every tab measures idleness
 * against that shared value. Without it, a forgotten background tab would reach
 * its own deadline and tear down the session for the tab the user is actively
 * working in.
 *
 * "Activity" is deliberately not limited to input. Watching a live fleet
 * dashboard without touching the machine is a primary use of this app, and
 * treating that as idle would log operators out mid-shift. So a tab that is on
 * screen keeps the session alive on its own (see VISIBLE_HEARTBEAT_MS).
 */
const IDLE_TIMEOUT_MS = 90 * 60 * 1000; // 90 minutes

/** Re-arming on every mousemove/scroll would mean thousands of writes a minute.
 *  Against a 90-minute window, refreshing at most once every 30s is
 *  indistinguishable in behaviour and enormously cheaper. */
const ACTIVITY_THROTTLE_MS = 30 * 1000;

/** How often an on-screen tab reports itself as active without any input.
 *  `visibilityState` is "visible" whenever the tab is displayed — it does NOT
 *  require the window to be focused — so a dashboard parked on a second monitor
 *  while the user works elsewhere keeps counting, which is the point. The idle
 *  clock therefore only really runs while the app is off screen. */
const VISIBLE_HEARTBEAT_MS = 60 * 1000;

const LAST_ACTIVITY_KEY = "lastActivityAt";

const readLastActivity = (): number => {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
};

const writeLastActivity = (at: number) => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    // Private mode / quota. The in-tab deadline below still applies.
  }
};

export default function IdleManager() {
  const dispatch = useDispatch<AppDispatch>();
  const auth = useAuth();
  // Held in a ref, never a dependency: useAuth() returns a fresh object on every
  // auth state change (a silent renew, for one), and re-running the effect for
  // that would hand an idle user a brand new idle window each time.
  const authRef = useRef(auth);
  authRef.current = auth;
  // Depend on *whether* we are authenticated, not on the token value: the token
  // rotates on every silent renew, and re-running this effect for that would
  // hand an idle user a fresh idle window every time their token refreshed.
  const isAuthenticated = useSelector(
    (s: RootState) => !!s.appAuthReducer.appToken,
  );

  useEffect(() => {
    if (!isAuthenticated) return;

    let timer: number | null = null;
    let lastWrite = 0;

    // Entering the app is itself activity — and it seeds the shared timestamp
    // for a first-ever session.
    const markActivity = (at: number) => {
      lastWrite = at;
      writeLastActivity(at);
    };

    const endSession = () => {
      // Clear local state first so nothing renders against a dead session, then
      // hand off to ZITADEL's end_session endpoint. Clearing locally is not
      // enough on its own: the ZITADEL SSO cookie would still be valid, so the
      // redirect to /login would silently sign the user straight back in, and
      // the session's refresh token would stay alive server-side.
      //
      // This must go through auth.signoutRedirect() rather than the UserManager
      // directly: the wrapper sets `activeNavigator`, which is what stops
      // ProtectedRoute from racing in with removeUser() and stripping the
      // id_token_hint out from under the in-flight end_session request.
      dispatch(logout());
      void authRef.current.signoutRedirect();
    };

    // Measured against the shared timestamp, so activity in any tab counts.
    // Re-deriving it on every wakeup is also what makes this correct across
    // laptop sleep and background-tab timer throttling, where a callback can
    // fire arbitrarily late relative to the real deadline.
    // `lastWrite` is the floor: if localStorage is unreadable (private mode,
    // quota, blocked cookies) readLastActivity() returns 0, and trusting that
    // alone would log the user out the instant they signed in.
    const remainingMs = () =>
      Math.max(readLastActivity(), lastWrite) + IDLE_TIMEOUT_MS - Date.now();

    const schedule = () => {
      if (timer !== null) window.clearTimeout(timer);
      const remaining = remainingMs();
      if (remaining <= 0) {
        endSession();
        return;
      }
      timer = window.setTimeout(schedule, remaining);
    };

    /** Extend the deadline, unless it has already passed. That guard matters:
     *  waking from sleep or un-hiding a tab can deliver a heartbeat or an input
     *  event before the throttled expiry callback gets its turn, and without it
     *  that would silently rescue a session that had already timed out. */
    const touch = () => {
      if (remainingMs() > 0) markActivity(Date.now());
      schedule();
    };

    const activityHandler = () => {
      if (Date.now() - lastWrite < ACTIVITY_THROTTLE_MS) return;
      touch();
    };

    // Passive watching: while the tab is on screen, report activity on a timer
    // even though nothing is being clicked. Guarded on visibility so a hidden
    // tab contributes nothing (browsers still fire throttled timers there).
    const heartbeat = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      activityHandler();
    }, VISIBLE_HEARTBEAT_MS);

    // Coming back to the tab is activity in its own right, and it should take
    // effect immediately rather than waiting out the next heartbeat.
    const visibilityHandler = () => {
      if (document.visibilityState !== "visible") return;
      touch();
    };
    document.addEventListener("visibilitychange", visibilityHandler);

    // `scroll` does not bubble, so without capture this would miss every
    // scrollable panel in the app and only see the document scrolling.
    // `wheel` covers trackpad scrolling that never moves the pointer.
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "wheel",
      "scroll",
    ];
    events.forEach((e) =>
      window.addEventListener(e, activityHandler, {
        passive: true,
        capture: true,
      }),
    );

    // Another tab reporting activity should push this tab's deadline out too.
    const storageHandler = (e: StorageEvent) => {
      if (e.key === LAST_ACTIVITY_KEY) schedule();
    };
    window.addEventListener("storage", storageHandler);

    markActivity(Date.now());
    schedule();

    return () => {
      events.forEach((e) =>
        window.removeEventListener(e, activityHandler, { capture: true }),
      );
      window.removeEventListener("storage", storageHandler);
      document.removeEventListener("visibilitychange", visibilityHandler);
      window.clearInterval(heartbeat);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [isAuthenticated, dispatch]);

  return null;
}
